import {
  AccountBalance,
  TrendingDown,
  TrendingUp,
  Warning,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  backfillPortfolioHistory,
  getPortfolioHistory,
  getPortfolioSummary,
  refreshPriceHistory,
  refreshPricesBatch,
  seedFxHistory,
} from "../../services/portfolioService";
import { useCurrency } from "../Auth/AuthContext";

const HISTORY_RANGES = [
  //{ label: "3D", days: 3 },
  { label: "5D", days: 5 },
  { label: "10D", days: 10 },
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
  { label: "2Y", days: 730 },
  { label: "All", days: 1825 },
];

const PortfolioOverviewPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDays, setHistoryDays] = useState(90);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);

  // Currency conversion hook
  const {
    selectedCurrency,
    convert,
    formatMoney,
    ratesReady,
    userCurrency,
    convertAndFormat,
  } = useCurrency();

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    const cancelled = { value: false };
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const data = await getPortfolioHistory(historyDays);
        if (!cancelled.value) setHistory(data);
      } catch (err) {
        console.error("Error fetching portfolio history:", err);
      } finally {
        if (!cancelled.value) setHistoryLoading(false);
      }
    };
    fetchHistory();
    return () => {
      cancelled.value = true;
    };
  }, [historyDays]);

  const handleBackfill = async (days = 365) => {
    setBackfilling(true);
    setBackfillMsg(null);
    try {
      // Step 1: seed historical FX rates for the requested date range
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      setBackfillMsg("Fetching historical FX rates…");
      const fxResult = await seedFxHistory({
        startDate: startDate.toISOString().split("T")[0],
      });
      const fxSummary =
        fxResult.pairs > 0
          ? `FX seeded (${fxResult.rows} rates, ${fxResult.pairs} pair(s)). `
          : "";

      // Step 2: refresh price history so recent dates (e.g. today-2) are in STOCK_PRICE_HISTORY
      setBackfillMsg(`${fxSummary}Refreshing price history…`);
      const histResult = await refreshPriceHistory("5y");
      const histSummary = `${histResult.refreshed} ticker(s) updated. `;

      // Step 3: run the backfill with date-accurate FX conversion
      setBackfillMsg(`${fxSummary}${histSummary}Building portfolio snapshots…`);
      const result = await backfillPortfolioHistory(days);
      setBackfillMsg(`${fxSummary}${histSummary}${result.message}`);

      // Step 4: reload history chart
      const data = await getPortfolioHistory(historyDays);
      setHistory(data);
    } catch (err) {
      setBackfillMsg("Backfill failed: " + err.message);
    } finally {
      setBackfilling(false);
    }
  };

  const fetchSummary = async () => {
    try {
      setLoading(true);

      // Initial load — renders quickly with cached (possibly stale) prices
      const initialSummary = await getPortfolioSummary();
      setSummary(initialSummary);
      setLoading(false);

      // Now refresh any stale prices in the background
      if (initialSummary?.positions?.length > 0) {
        const tickers = [
          ...new Set(initialSummary.positions.map((p) => p.ticker_symbol)),
        ];
        setPricesLoading(true);
        try {
          await refreshPricesBatch(tickers);
          // Re-fetch summary so current_value / unrealized_pl reflect fresh prices
          const freshSummary = await getPortfolioSummary();
          setSummary(freshSummary);
        } catch (err) {
          console.error("Error refreshing prices for portfolio summary:", err);
          // Not fatal — summary with stale prices is still shown
        } finally {
          setPricesLoading(false);
        }
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
      setError("Failed to load portfolio summary");
      setLoading(false);
    }
  };

  const formatPercent = (value) => {
    const formatted = Math.abs(value).toFixed(2);
    return `${value >= 0 ? "+" : "-"}${formatted}%`;
  };

  const getConvertedOverall = () => {
    if (!summary?.positions || !ratesReady) {
      return (
        summary?.overall || {
          total_cost: 0,
          total_value: 0,
          total_pl: 0,
          total_pl_percent: 0,
          total_positions: 0,
        }
      );
    }

    let totalCost = 0;
    let totalValue = 0;

    var currencyUsed;
    if (selectedCurrency === "DEFAULT") {
      currencyUsed = userCurrency;
    } else {
      currencyUsed = selectedCurrency;
    }

    summary.positions.forEach((pos) => {
      totalCost += convert(pos.total_cost, pos.currency, currencyUsed);
      totalValue += convert(pos.current_value, pos.currency, currencyUsed);
    });

    const totalPl = totalValue - totalCost;
    const totalPlPercent = totalCost > 0 ? (totalPl / totalCost) * 100 : 0;

    return {
      total_cost: totalCost,
      total_value: totalValue,
      total_pl: totalPl,
      total_pl_percent: totalPlPercent,
      total_positions: summary.positions.length,
    };
  };

  const getConvertedBrokerSummary = () => {
    if (!summary?.positions || !ratesReady) return [];

    const brokerMap = {};

    summary.positions.forEach((pos) => {
      const broker = pos.broker_name;
      if (!brokerMap[broker]) {
        brokerMap[broker] = {
          broker,
          total_cost: 0,
          total_value: 0,
          total_positions: 0,
          currencies: new Set(),
          currency: summary.currencies[broker],
        };
      }

      brokerMap[broker].total_cost += convert(
        pos.total_cost,
        pos.currency,
        brokerMap[broker].currency,
      );
      brokerMap[broker].total_value += convert(
        pos.current_value,
        pos.currency,
        brokerMap[broker].currency,
      );
      brokerMap[broker].total_positions += 1;
      brokerMap[broker].currencies.add(pos.currency);
    });

    return Object.values(brokerMap).map((broker) => ({
      ...broker,
      total_pl: broker.total_value - broker.total_cost,
      total_pl_percent:
        broker.total_cost > 0
          ? ((broker.total_value - broker.total_cost) / broker.total_cost) * 100
          : 0,
      is_multi_currency: broker.currencies.size > 1,
      currencies_list: Array.from(broker.currencies).join(", "),
    }));
  };

  const getConvertedCurrencySummary = () => {
    if (!summary?.positions || !ratesReady) return [];

    const currencyMap = {};
    summary.positions.forEach((pos) => {
      const currency = pos.currency || "USD";
      if (!currencyMap[currency]) {
        currencyMap[currency] = {
          currency,
          total_cost: 0,
          total_value: 0,
          total_pl: 0,
          total_pl_percent: 0,
          total_positions: 0,
          total_shares: 0,
        };
      }
      currencyMap[currency].total_cost += parseFloat(pos.total_cost || 0);
      currencyMap[currency].total_value += parseFloat(pos.current_value || 0);
      currencyMap[currency].total_pl += parseFloat(pos.unrealized_pl || 0);
      currencyMap[currency].total_positions += 1;
      currencyMap[currency].total_shares += parseFloat(pos.net_quantity || 0);
    });

    return Object.values(currencyMap).map((curr) => ({
      ...curr,
      total_pl_percent:
        curr.total_cost > 0
          ? (curr.total_pl_percent = (curr.total_pl / curr.total_cost) * 100)
          : 0,
    }));
  };

  const convertedOverall = getConvertedOverall();
  const convertedBrokers = getConvertedBrokerSummary();
  const convertedCurrency = getConvertedCurrencySummary();

  // Convert chart data to the currently selected display currency
  const displayCurrency =
    selectedCurrency === "DEFAULT" ? userCurrency : selectedCurrency;
  const chartData =
    ratesReady && history.length > 0
      ? history.map((pt) => ({
          ...pt,
          total_value: convert(
            parseFloat(pt.total_value),
            pt.currency,
            displayCurrency,
          ),
          total_cost: convert(
            parseFloat(pt.total_cost),
            pt.currency,
            displayCurrency,
          ),
        }))
      : history;

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!summary) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {pricesLoading && (
        <Typography
          variant="caption"
          sx={{ color: "rgba(255, 8, 8, 0.75)", mt: 0.5 }}
        >
          Refreshing prices…
        </Typography>
      )}
      {/* Page Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600}>
          Portfolio Summary
        </Typography>
        <Chip
          label={`Displaying in ${selectedCurrency === "DEFAULT" ? userCurrency + " (DEFAULT)" : selectedCurrency}`}
          size="small"
          variant="outlined"
          sx={{ color: "text.secondary", borderColor: "divider" }}
        />
      </Box>

      {/* Portfolio History Chart */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6" fontWeight={600}>
            Portfolio Value History
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <ToggleButtonGroup
              value={historyDays}
              exclusive
              size="small"
              onChange={(_, val) => {
                if (val !== null) setHistoryDays(val);
              }}
            >
              {HISTORY_RANGES.map((r) => (
                <ToggleButton
                  key={r.days}
                  value={r.days}
                  sx={{ px: 1.5, py: 0.5, fontSize: "0.75rem" }}
                >
                  {r.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Tooltip title="Step 1: fetches historical ECB FX rates (frankfurter.app) for the full date range so currency conversion is period-accurate. Step 2: rebuilds portfolio value snapshots using those rates.">
              <Button
                id="backfill-history-btn"
                size="small"
                variant="outlined"
                onClick={() => handleBackfill(1825)}
                disabled={backfilling}
                sx={{
                  fontSize: "0.7rem",
                  py: 0.5,
                  px: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {backfilling ? "Backfilling…" : "Backfill History"}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {backfillMsg && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            onClose={() => setBackfillMsg(null)}
          >
            {backfillMsg}
          </Alert>
        )}

        {historyLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={220}
          >
            <CircularProgress size={32} />
          </Box>
        ) : chartData.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height={220}
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              No history data yet. Click "Backfill History" to generate from
              your price data.
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart
              data={chartData}
              margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
              <XAxis
                dataKey="date"
                tickFormatter={(d) => {
                  try {
                    return format(
                      parseISO(d),
                      historyDays <= 90 ? "MMM d" : "MMM yy",
                    );
                  } catch {
                    return d;
                  }
                }}
                tick={{ fontSize: 11 }}
                tickLine={false}
              />
              <YAxis
                domain={["auto", "auto"]}
                tickFormatter={(v) =>
                  v >= 1_000_000
                    ? `${(v / 1_000_000).toFixed(1)}M`
                    : v >= 1_000
                      ? `${(v / 1_000).toFixed(0)}k`
                      : v.toFixed(0)
                }
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={52}
              />
              <RechartsTooltip
                formatter={(value, name) => [
                  new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: displayCurrency || "USD",
                    maximumFractionDigits: 0,
                  }).format(value),
                  name === "total_value" ? "Portfolio Value" : "Total Invested",
                ]}
                labelFormatter={(label) => {
                  try {
                    return format(parseISO(label), "MMMM d, yyyy");
                  } catch {
                    return label;
                  }
                }}
                contentStyle={{ fontSize: 12 }}
              />
              <Legend
                formatter={(value) =>
                  value === "total_value" ? "Portfolio Value" : "Total Invested"
                }
                wrapperStyle={{ fontSize: 12 }}
              />
              <Line
                type="monotone"
                dataKey="total_value"
                stroke="#667eea"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="total_cost"
                stroke="#aaa"
                strokeWidth={1.5}
                strokeDasharray="4 2"
                dot={false}
                activeDot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Overall Summary Card */}
      <Card
        sx={{
          mb: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                Total Invested
              </Typography>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ color: "white", mt: 0.5 }}
              >
                {formatMoney(
                  convertedOverall.total_cost,
                  selectedCurrency === "DEFAULT"
                    ? userCurrency
                    : selectedCurrency,
                  selectedCurrency,
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                Current Value
              </Typography>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ color: "white", mt: 0.5 }}
              >
                {formatMoney(
                  convertedOverall.total_value,
                  selectedCurrency === "DEFAULT"
                    ? userCurrency
                    : selectedCurrency,
                  selectedCurrency,
                )}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                Total Profit/Loss
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                {convertedOverall.total_pl >= 0 ? (
                  <TrendingUp sx={{ color: "white" }} />
                ) : (
                  <TrendingDown sx={{ color: "white" }} />
                )}
                <Typography
                  variant="h5"
                  fontWeight={600}
                  sx={{ color: "white" }}
                >
                  {formatMoney(
                    Math.abs(convertedOverall.total_pl),
                    selectedCurrency === "DEFAULT"
                      ? userCurrency
                      : selectedCurrency,
                    selectedCurrency,
                  )}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.9)", mt: 0.5 }}
              >
                {convertedOverall.total_pl >= 0 ? "+" : "-"}
                {formatPercent(convertedOverall.total_pl_percent)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                Total Positions
              </Typography>
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ color: "white", mt: 0.5 }}
              >
                {convertedOverall.total_positions}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* By Broker Summary — FIXED: Handles multi-currency */}
      {convertedBrokers.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box p={2} borderBottom={1} borderColor="divider">
            <Typography variant="h6" fontWeight={600}>
              By Broker
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Broker</TableCell>
                  <TableCell align="right">Positions</TableCell>
                  <TableCell align="right">Total Invested</TableCell>
                  <TableCell align="right">Current Value</TableCell>
                  <TableCell align="right">Profit/Loss</TableCell>
                  {/* <TableCell align="right">
                    ≈{" "}
                    {selectedCurrency === "DEFAULT"
                      ? userCurrency
                      : selectedCurrency}{" "}
                    Value
                  </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {convertedBrokers.map((broker) => (
                  <TableRow key={broker.broker}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={broker.broker}
                          size="small"
                          icon={<AccountBalance />}
                        />
                        {broker.is_multi_currency && (
                          <Tooltip
                            title={`Multi-currency broker: ${broker.currencies_list}`}
                          >
                            <Warning
                              fontSize="small"
                              sx={{ color: "warning.main" }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      {broker.is_multi_currency && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            mt: 0.5,
                          }}
                        >
                          {broker.currencies_list} → {broker.currency}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {broker.total_positions}
                    </TableCell>
                    <TableCell align="right">
                      {convertAndFormat(broker.total_cost, broker.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {convertAndFormat(broker.total_value, broker.currency)}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          color:
                            broker.total_pl >= 0
                              ? "success.main"
                              : "error.main",
                          fontWeight: 600,
                        }}
                      >
                        {broker.total_pl >= 0 ? "+" : ""}
                        {convertAndFormat(broker.total_pl, broker.currency)}
                        <Typography variant="caption" display="block">
                          ({formatPercent(broker.total_pl_percent)})
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* By Currency Summary */}
      {/* {summary.by_currency && summary.by_currency.length > 0 && ( */}
      {convertedCurrency.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box p={2} borderBottom={1} borderColor="divider">
            <Typography variant="h6" fontWeight={600}>
              By Currency
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Positions</TableCell>
                  <TableCell align="right">Total Invested</TableCell>
                  <TableCell align="right">Current Value</TableCell>
                  <TableCell align="right">Profit/Loss</TableCell>
                  {/* <TableCell align="right">
                    ≈{" "}
                    {selectedCurrency === "DEFAULT"
                      ? userCurrency
                      : selectedCurrency}{" "}
                    Value
                  </TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {convertedCurrency.map((curr) => (
                  <TableRow key={curr.currency}>
                    <TableCell>
                      <Chip
                        label={curr.currency}
                        size="small"
                        icon={<AccountBalance />}
                      />
                    </TableCell>
                    <TableCell align="right">{curr.total_positions}</TableCell>
                    <TableCell align="right">
                      {convertAndFormat(curr.total_cost, curr.currency)}
                    </TableCell>
                    <TableCell align="right">
                      {convertAndFormat(curr.total_value, curr.currency)}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          color:
                            curr.total_pl >= 0 ? "success.main" : "error.main",
                          fontWeight: 600,
                        }}
                      >
                        {curr.total_pl >= 0 ? "+" : ""}
                        {convertAndFormat(curr.total_pl, curr.currency)}
                        <Typography variant="caption" display="block">
                          ({formatPercent(curr.total_pl_percent)})
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* All Positions Detail */}
      {summary.positions && summary.positions.length > 0 && (
        <Paper>
          <Box p={2} borderBottom={1} borderColor="divider">
            <Typography variant="h6" fontWeight={600}>
              All Positions
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Broker</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Avg Price</TableCell>
                  <TableCell align="right">Total Invested</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Profit/Loss</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.positions.map((pos, index) => (
                  <TableRow
                    key={`${pos.ticker_symbol}-${pos.broker_id}-${index}`}
                  >
                    <TableCell>
                      <Link
                        to={`/stocks/${pos.ticker_symbol}`}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ "&:hover": { color: "primary.main" } }}
                        >
                          {pos.ticker_symbol}
                        </Typography>
                      </Link>
                      <Typography variant="caption" color="text.secondary">
                        {pos.company_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{pos.broker_name}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat("en-US").format(pos.net_quantity)}
                    </TableCell>

                    {/* Avg Price */}
                    <TableCell align="right">
                      <Typography variant="body2">
                        {convertAndFormat(pos.avg_price, pos.currency)}
                      </Typography>
                      {/* {pos.currency !== selectedCurrency && (
                        <Typography variant="caption" color="text.secondary">
                          (
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: pos.currency,
                            maximumFractionDigits: 2,
                          }).format(pos.avg_price)}
                          )
                        </Typography>
                      )} */}
                      {selectedCurrency !== "DEFAULT" &&
                        pos.currency !== selectedCurrency && (
                          <Typography variant="caption" color="text.secondary">
                            (
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: pos.currency,
                              maximumFractionDigits: 2,
                            }).format(pos.avg_price)}
                            )
                          </Typography>
                        )}
                    </TableCell>

                    {/* Total Invested */}
                    <TableCell align="right">
                      <Typography variant="body2">
                        {/* {formatMoney(pos.current_value, pos.currency)} */}
                        {convertAndFormat(pos.total_cost, pos.currency)}
                      </Typography>
                      {selectedCurrency !== "DEFAULT" &&
                        pos.currency !== selectedCurrency && (
                          <Typography variant="caption" color="text.secondary">
                            (
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: pos.currency,
                              maximumFractionDigits: 2,
                            }).format(pos.total_cost)}
                            )
                          </Typography>
                        )}
                    </TableCell>

                    {/* Value */}
                    <TableCell align="right">
                      <Typography variant="body2">
                        {/* {formatMoney(pos.current_value, pos.currency)} */}
                        {convertAndFormat(pos.current_value, pos.currency)}
                      </Typography>
                      {selectedCurrency !== "DEFAULT" &&
                        pos.currency !== selectedCurrency && (
                          <Typography variant="caption" color="text.secondary">
                            (
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: pos.currency,
                              maximumFractionDigits: 2,
                            }).format(pos.current_value)}
                            )
                          </Typography>
                        )}
                    </TableCell>

                    <TableCell align="right">
                      <Box
                        sx={{
                          color:
                            pos.unrealized_pl >= 0
                              ? "success.main"
                              : "error.main",
                          fontWeight: 600,
                        }}
                      >
                        {pos.unrealized_pl >= 0 ? "+" : ""}
                        {/* {formatMoney(pos.unrealized_pl, pos.currency)} */}
                        {convertAndFormat(pos.unrealized_pl, pos.currency)}
                        <Typography variant="caption" display="block">
                          ({formatPercent(pos.unrealized_pl_percent)})
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default PortfolioOverviewPage;
